import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GraduationCap, Users, Handshake, ArrowUpRight } from 'lucide-react';
import { useBookingModal } from '../contexts/BookingModalContext';

export const OutreachPage: React.FC = () => {
    const mainRef = useRef<HTMLDivElement>(null);
    const { openModal } = useBookingModal();

    useEffect(() => {
        gsap.registerPlugin(ScrollTrigger);
        
        const ctx = gsap.context(() => {
            gsap.fromTo('.hero-tagline', 
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: 'power3.out',
                    clearProps: 'all'
                }
            );

            gsap.fromTo('.feature-card', 
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    stagger: 0.2,
                    ease: 'power2.out',
                    clearProps: 'all',
                    scrollTrigger: {
                        trigger: '.feature-cards-section',
                        start: 'top 80%',
                        toggleActions: 'play none none none'
                    }
                }
            );
        }, mainRef);

        return () => ctx.revert();
    }, []);

    const opportunities = [
        {
            title: 'High School Internships',
            icon: <GraduationCap className="w-8 h-8 text-white" />,
            description: 'We offer engaging opportunities for high school students to learn about mental health care, organizational operations, and community service. Gain valuable experience in a supportive environment.',
            color: '#d68800'
        },
        {
            title: 'College Internships',
            icon: <Users className="w-8 h-8 text-white" />,
            description: 'Designed for undergraduate and graduate students pursuing degrees in counseling, social work, or related fields. Our clinical and administrative internships provide hands-on training and mentorship from experienced professionals.',
            color: '#d68800',
            link: 'https://jobs.gusto.com/postings/michelle-r-simmons-lpc-llc-student-intern-09d2c42d-6913-4f39-9522-317030a26018'
        },
        {
            title: 'Grant Partnerships',
            icon: <Handshake className="w-8 h-8 text-white" />,
            description: 'We actively collaborate with community organizations, schools, and foundations through grant-funded initiatives to expand access to mental health services and community outreach programs.',
            color: '#891719'
        }
    ];

    return (
        <div ref={mainRef} className="outreach-page bg-mscg-light-gray min-h-screen">
            {/* Hero Section */}
            <section className="relative min-h-[50vh] flex items-center bg-mscg-dark-gray pt-20">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
                    backgroundSize: '30px 30px'
                }}></div>
                
                <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                        Outreach <span className="text-[#d68800]">Opportunities</span>
                    </h1>
                    <p className="hero-tagline text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                        At MSCG, we believe in building stronger communities through education, partnership, and hands-on learning. Explore how you can partner with us to make a lasting impact.
                    </p>
                </div>
            </section>

            {/* Opportunities Section */}
            <section className="feature-cards-section py-24 relative bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {opportunities.map((opp, index) => (
                            <div 
                                key={index} 
                                className="feature-card bg-mscg-light-gray rounded-3xl p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-2 border-transparent hover:border-[#d68800] group flex flex-col"
                            >
                                <div 
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8 shadow-lg transform group-hover:scale-110 transition-transform duration-300"
                                    style={{ backgroundColor: opp.color }}
                                >
                                    {opp.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-mscg-dark-gray mb-4">
                                    {opp.title}
                                </h3>
                                <p className="text-neutral-600 leading-relaxed mb-8 flex-grow">
                                    {opp.description}
                                </p>
                                {opp.link ? (
                                    <a 
                                        href={opp.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-mscg-dark-gray font-bold group-hover:text-[#d68800] transition-colors mt-auto"
                                    >
                                        Apply Now <ArrowUpRight className="w-5 h-5" />
                                    </a>
                                ) : (
                                    <button 
                                        onClick={openModal}
                                        className="inline-flex items-center gap-2 text-mscg-dark-gray font-bold group-hover:text-[#d68800] transition-colors mt-auto"
                                    >
                                        Learn More <ArrowUpRight className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};
